import React from "react";
import { ParsedUrlQuery } from "querystring";
import { gql } from "@apollo/client";
import { useRouter } from "next/router";
import { GetServerSideProps } from "next";
import { initializeApollo, addApolloState } from "init/apolloClient";
import { BuildingPageQuery_buildingOrRedirect_building } from "types/pages/building/BuildingPageQuery";
import BuildingPage from "components/BuildingPage";

const BUILDING_PAGE_QUERY = gql`
  query BuildingPageQuery($slug: String!, $identity: ID) {
    buildingOrRedirect(slug: $slug, identity: $identity) {
      redirect
      building {
        id
        identity
        address
        addresses {
          id: identity
          address
        }
        fullAddress
        cmsContent {
          title
          content
        }
        propertyName
        city
        state
        zip
        slug
        lat
        lng
        minSquareFeet
        maxSquareFeet
        minEstimatedSeats
        maxEstimatedSeats
        minEstimatedMonthlyPrice
        maxEstimatedMonthlyPrice
        details
        hasAttendedLobby
        hasRoundTheClockAccess
        hasVirtualTours
        hasTurnstile
        coverImage {
          id
          url(size: "original")
        }
        images2 {
          nodes {
            id
            url(size: "slideshow_small")
          }
        }
        market {
          id
          name
          externalId
          seoString
        }
        neighborhood {
          id
          name
          rootSlug
          yearlyPricePerFoot
          externalId
        }
        submarket {
          id
          name
          externalId
          seoString
        }
        listings {
          id
          floorAndSuite
          squareFeet
          estimatedSquareFeet
          maxOccupancy
          estimatedMaxOccupancy
          estimatedMonthlyPrice
          displayMonthlyPrice
          displayPSF
          estimatedPsf
          accuratePsf
          details
          leaseType
          leaseTerm
          status
          description
          naturalLight
          hasWetPantry
          floorMaterial
          conferenceRooms
          hasExposedCeiling
          hasPrivateBathroom
          dataChangedAt
          brokerInsights {
            id
            content
            updatedAt
            author {
              displayName
              profilePicURL
            }
          }
          images2 {
            nodes {
              id
              url(size: "slideshow_small")
            }
          }
        }
        nearbyBuildings {
          id
          address
          slug
          coverImage {
            id
            url(size: "slideshow_big")
          }
          minSquareFeet
          maxSquareFeet
          minEstimatedMonthlyPrice
          maxEstimatedMonthlyPrice
          neighborhood {
            id
            name
          }
        }
        virtualTours {
          id
          matterportId
          description
          buildingId
        }
      }
    }
  }
`;

interface IProps {
  state: string;
  city: string;
  address: string;
  selectedListingId: string;
  building: BuildingPageQuery_buildingOrRedirect_building;
  prevUrl?: string;
}

export const PublicBuildingPage = ({
  state,
  city,
  address,
  selectedListingId,
  building,
  prevUrl,
}: IProps) => {
  // Fragments (the bit after the hash) never get sent to the server, by design. So, in order to transition from using fragments to indicate which listing we're looking at to using query params (so that we can pre-render the page), we need to handle anyone coming in with an old link using a hash by checking client-side and then doing another server-side fetch to get the listing data. This isn't ideal, but modern links have been updated so this shouldn't be regularly used
  const router = useRouter();
  const [path, fragment] = router.asPath.split("#");
  if (fragment) {
    router.replace(`${path}?listingId=${fragment}`);
  }

  const selectedListing = selectedListingId
    ? building?.listings?.find((listing) => listing?.id === selectedListingId)
    : null;

  return (
    <div>
      <BuildingPage
        building={building}
        city={city}
        state={state}
        address={address}
        selectedListing={selectedListing}
        prevUrl={prevUrl}
      />
    </div>
  );
};

interface Params extends ParsedUrlQuery {
  slug: string[];
}

export const getServerSideProps: GetServerSideProps<Params> = async ({
  params,
  query,
  res,
  req,
}) => {
  let slug = params?.slug || [];
  if (typeof slug === "string") slug = [slug]; // this line is needed since params.slug can be string | string[] | undefined.
  const apolloClient = initializeApollo();
  const { listingId: selectedListingId = null } = query;
  const state = slug[0] || "";
  const city = slug[1] || "";
  const address = slug[2] || "";
  const buildingIdentity = slug[3] || undefined;

  const { error, data } = await apolloClient.query({
    query: BUILDING_PAGE_QUERY,
    variables: {
      slug: `/${state}/${city}/${address}`,
      identity: buildingIdentity,
    },
  });

  if (error) throw new Error(error);

  if (data?.buildingOrRedirect?.redirect) {
    let location = `/building${data?.buildingOrRedirect?.redirect}`;
    if (selectedListingId) location += `?listingId=${selectedListingId}`;
    res.writeHead(301, { location });
    res.end();
  }

  if (!data.buildingOrRedirect?.building) {
    return { notFound: true };
  }

  return addApolloState(apolloClient, {
    props: {
      prevUrl: req?.headers?.referer || null,
      state,
      city,
      address,
      selectedListingId,
      error: error ?? null,
      building: data?.buildingOrRedirect?.building ?? null,
    },
  });
};

export default PublicBuildingPage;
